import { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updateTenant } from "../../../store/authSlice";
import clinicAPI from "../../../services/clinicApi";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { isClubOwner, normalizeRole, hasAdminAccess } from "../../../utils/roles";
import AdminPatientImport from "../AdminPatients/AdminPatientImport";
import SettingsPortalBrandingPanel from "./SettingsPortalBrandingPanel";
import SettingsAcquiringPanel from "./SettingsAcquiringPanel";
import AttendancePollSettingsPanel from "./AttendancePollSettingsPanel";
import TrainerNotifySettingsPanel from "./TrainerNotifySettingsPanel";
import { AdminPushBlock } from "../AdminPortal/staffProfileShared";
import {
  Button,
  Field,
  Input,
  PhoneInput,
  SkeletonCard,
  Tabs,
  Textarea,
  useToast,
  Avatar,
} from "../../ui";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import {
  CRM_USER_GUIDE_HINT,
  CRM_USER_GUIDE_LABEL,
  CRM_USER_GUIDE_URL,
} from "../../../constants/crmGuide";
import { HubFormCard, HubScreen, HubSegment } from "../AdminPortal/HubPwa";
import { resolvePlanAccess } from "../../../utils/planEntitlements";
import { isClubAcquiringEmailAllowed } from "../../../utils/clubAcquiringAccess";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import md from "../common/manageDesktop.module.css";

const DEFAULT = {
  name: "",
  address: "",
  phone: "",
  logo_url: "",
  subdomain: "",
  status: "",
  timezone: "Europe/Moscow",
  slot_step_minutes: 15,
  slot_buffer_minutes: 0,
  min_booking_lead_hours: 2,
  cancellation_lead_hours: 24,
  patient_reminder_enabled: true,
  auto_approve_registrations: true,
  portal_accent_color: null,
  portal_cover_url: null,
  portal_font_family: null,
  portal_theme_mode: "light",
  portal_description: "",
  portal_gallery: [],
  website_enabled: false,
  website_template: "classic",
  website_content: {
    headline: "",
    subheadline: "",
    cta_label: "Записаться на пробное",
    about_title: "О клубе",
    about_text: "",
    show_gallery: true,
    show_trial_form: true,
    contacts_note: "",
  },
  website_templates: [{ id: "classic", label: "Классика" }],
};

const STATUS_LABELS = {
  trial: "Пробный",
  active: "Активна",
  suspended: "Приостановлена",
};

const BASE_TABS = [
  { id: "general", label: "Общие" },
  { id: "portal", label: "Персонализация" },
  { id: "notify", label: "Уведомления" },
  { id: "data", label: "Данные" },
];

const REMOVED_TABS = new Set([
  "structure",
  "team",
  "booking",
  "venues",
  "directions",
  "registration",
  "website",
]);

export default function AdminSettings() {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isApp = useAdminHubUi();
  const { tenant: authTenant, entitlements, billing, profile, role, club } = useSelector(
    (state) => state.auth,
  );
  const owner = isClubOwner({ role, profileRole: profile?.role, club });
  const plan = useMemo(
    () => resolvePlanAccess(entitlements, authTenant, billing),
    [entitlements, authTenant, billing],
  );
  const showPaymentsTab =
    plan.onlineAcquiring && isClubAcquiringEmailAllowed(profile?.email);
  const visibleTabs = useMemo(() => {
    const tabs = BASE_TABS.filter((t) => {
      if (t.id === "portal") return plan.showPortalBrandingTab;
      return true;
    });
    if (showPaymentsTab) {
      const dataIdx = tabs.findIndex((t) => t.id === "data");
      const paymentTab = { id: "payments", label: "Оплата" };
      if (dataIdx >= 0) tabs.splice(dataIdx, 0, paymentTab);
      else tabs.push(paymentTab);
    }
    return tabs;
  }, [plan.showPortalBrandingTab, showPaymentsTab]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const legacyTab = REMOVED_TABS.has(tabFromUrl) ? "general" : tabFromUrl;
  const validTab = visibleTabs.some((t) => t.id === legacyTab) ? legacyTab : "general";
  const [tab, setTab] = useState(validTab);

  useEffect(() => {
    if (validTab !== tab) setTab(validTab);
    if (tabFromUrl && REMOVED_TABS.has(tabFromUrl)) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [validTab, tab, tabFromUrl, searchParams, setSearchParams]);

  const setTabAndUrl = (id) => {
    setTab(id);
    const next = new URLSearchParams(searchParams);
    if (id === "general") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const [form, setForm] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    clinicAPI
      .getTenantSettings()
      .then((r) => {
        if (r.tenant) setForm({ ...DEFAULT, ...r.tenant });
      })
      .catch((e) => toast.error(e.response?.data?.error || "Ошибка загрузки"))
      .finally(() => setLoading(false));
  }, [toast]);

  const buildPayload = () => {
    const payload = { ...form };
    delete payload.subdomain;
    delete payload.status;
    delete payload.id;
    delete payload.members_count;
    delete payload.createdAt;
    delete payload.website_enabled;
    delete payload.website_template;
    delete payload.website_content;
    delete payload.website_templates;
    return payload;
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const r = await clinicAPI.updateTenantSettings(buildPayload());
      setForm({ ...DEFAULT, ...r.tenant });
      toast.success("Настройки сохранены");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const mergeTenant = (tenant) => {
    if (!tenant) return;
    setForm((f) => ({ ...f, ...tenant }));
    dispatch(
      updateTenant({
        portal_accent_color: tenant.portal_accent_color ?? null,
        portal_cover_url: tenant.portal_cover_url ?? null,
        portal_font_family: tenant.portal_font_family ?? null,
        portal_theme_mode: tenant.portal_theme_mode === "dark" ? "dark" : "light",
        portal_description: tenant.portal_description ?? null,
        portal_gallery: tenant.portal_gallery ?? [],
        website_enabled: !!tenant.website_enabled,
        website_template: tenant.website_template || "classic",
        website_content: tenant.website_content,
        name: tenant.name,
        logo_url: tenant.logo_url,
        address: tenant.address,
        phone: tenant.phone,
      }),
    );
  };

  const saveButton = (fullWidth = false) => (
    <div className={isApp ? aa.settingsHubSaveRow : undefined}>
      <Button
        onClick={save}
        disabled={saving}
        className={fullWidth ? aa.hubPrimaryCta : undefined}
        style={fullWidth ? { width: "100%" } : undefined}
      >
        {saving ? "Сохранение…" : "Сохранить"}
      </Button>
    </div>
  );

  const generalFieldsDesktop = (
    <div className={md.desktopSettingsGeneralCard}>
      <div className={md.desktopSettingsGeneralHead}>
        <h2 className={md.desktopSettingsGeneralTitle}>Профиль клуба</h2>
        <p className={md.desktopSettingsGeneralHint}>
          Название, контакты и короткое описание — это видят клиенты в приложении
        </p>
      </div>
      <div className={md.desktopSettingsFormStack}>
        <div className={md.desktopSettingsFormGrid}>
          <Field label="Название клуба">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </Field>
          <Field label="Телефон">
            <PhoneInput
              value={form.phone || ""}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
          </Field>
        </div>
        <Field label="Адрес">
          <Input
            value={form.address || ""}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="Город, улица, корпус"
          />
        </Field>
        <Field label="О клубе">
          <Textarea
            value={form.portal_description || ""}
            onChange={(e) =>
              setForm({ ...form, portal_description: e.target.value })
            }
            rows={4}
            placeholder="Краткое описание для клиентов: направления, атмосфера, как добраться"
          />
        </Field>
      </div>
    </div>
  );

  const generalFieldsApp = (
    <>
      <Field label="Название клуба">
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
      </Field>
      <Field label="Телефон">
        <Input
          value={form.phone || ""}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </Field>
      <Field label="Адрес">
        <Input
          value={form.address || ""}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </Field>
      <Field label="О клубе">
        <Textarea
          value={form.portal_description || ""}
          onChange={(e) =>
            setForm({ ...form, portal_description: e.target.value })
          }
          rows={4}
          placeholder="Краткое описание для клиентов: направления, атмосфера, как добраться"
        />
      </Field>
    </>
  );

  const panelClass = isApp ? s.settingsPanel : md.desktopSettingsSection;

  const tabPanels = (
    <>
      {tab === "general" && (
        <form onSubmit={save} className={panelClass}>
          {isApp ? (
            <HubFormCard title="Клуб">{generalFieldsApp}</HubFormCard>
          ) : (
            generalFieldsDesktop
          )}
          {isApp ? saveButton(true) : null}
        </form>
      )}

      {tab === "portal" && (
        <SettingsPortalBrandingPanel
          accentColor={form.portal_accent_color}
          coverUrl={form.portal_cover_url}
          logoUrl={form.logo_url}
          fontFamily={form.portal_font_family}
          themeMode={form.portal_theme_mode}
          galleryUrls={form.portal_gallery}
          fullBranding={plan.fullBranding}
          onUpdated={mergeTenant}
          compact={isApp}
          desktop={!isApp}
          rootClassName={!isApp ? md.desktopSettingsBrandPage : ""}
        />
      )}

      {tab === "payments" && showPaymentsTab && (
        <SettingsAcquiringPanel compact={isApp} desktop={!isApp} />
      )}

      {tab === "notify" && (
        <form onSubmit={save} className={panelClass}>
          {isApp ? (
            <HubFormCard title="Клиентам">
              <p className={s.onboardingSheetHint}>
                Автоматические напоминания о предстоящих занятиях из расписания
                клуба.
              </p>
              <label className={s.settingsToggleRow}>
                <input
                  type="checkbox"
                  checked={form.patient_reminder_enabled !== false}
                  onChange={(e) =>
                    setForm({ ...form, patient_reminder_enabled: e.target.checked })
                  }
                />
                <span>
                  <strong>Напоминание перед тренировкой</strong>
                  <span className={s.branchCheckAddr}>
                    За 4 часа до начала занятия — push в приложении клиента. Если
                    push не подключён, уйдёт email с датой, временем и тренером.
                  </span>
                </span>
              </label>
            </HubFormCard>
          ) : (
            <section className={md.desktopSettingsNotifyCard}>
              <h3 className={md.desktopSettingsSubheading}>Клиентам</h3>
              <p className={md.desktopGroupSettingsLead}>
                Автоматические напоминания о предстоящих занятиях из расписания
                клуба.
              </p>
              <label className={s.settingsToggleRow}>
                <input
                  type="checkbox"
                  checked={form.patient_reminder_enabled !== false}
                  onChange={(e) =>
                    setForm({ ...form, patient_reminder_enabled: e.target.checked })
                  }
                />
                <span>
                  <strong>Напоминание перед тренировкой</strong>
                  <span className={s.branchCheckAddr}>
                    За 4 часа до начала занятия — push в приложении клиента. Если
                    push не подключён, уйдёт email с датой, временем и тренером.
                  </span>
                </span>
              </label>
            </section>
          )}

          {plan.polls ? (
            <AttendancePollSettingsPanel compact={isApp} />
          ) : null}

          <TrainerNotifySettingsPanel compact={isApp} />

          {isApp ? (
            <HubFormCard title="Мне на этом устройстве">
              <AdminPushBlock />
            </HubFormCard>
          ) : (
            <section className={md.desktopSettingsNotifyCard}>
              <h3 className={md.desktopSettingsSubheading}>Мне на этом устройстве</h3>
              <AdminPushBlock />
            </section>
          )}

          {isApp ? saveButton(true) : null}
        </form>
      )}

      {tab === "data" && (
        <div className={panelClass}>
          {isApp ? (
            <div className={aa.hubListGroup}>
              <button
                type="button"
                className={aa.hubLinkRow}
                onClick={() => setImportOpen(true)}
              >
                <span>
                  <strong>Импорт клиентов</strong>
                  <span className={aa.hubHint}>CSV: full_name, email, phone…</span>
                </span>
              </button>
              <button
                type="button"
                className={aa.hubLinkRow}
                onClick={async () => {
                  try {
                    const res = await clinicAPI.exportPatientsCsv();
                    const url = URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "patients.csv";
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("CSV скачан");
                  } catch (e) {
                    toast.error(e.response?.data?.error || "Ошибка");
                  }
                }}
              >
                <span>
                  <strong>Экспорт клиентов</strong>
                  <span className={aa.hubHint}>Скачать реестр CSV</span>
                </span>
              </button>
              <Link to="/manage/app/clients" className={aa.hubLinkRow}>
                <span>
                  <strong>Реестр клиентов</strong>
                  <span className={aa.hubHint}>Поиск и карточки</span>
                </span>
              </Link>
              <a
                href={CRM_USER_GUIDE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={aa.hubLinkRow}
              >
                <span>
                  <strong>{CRM_USER_GUIDE_LABEL}</strong>
                  <span className={aa.hubHint}>{CRM_USER_GUIDE_HINT}</span>
                </span>
              </a>
            </div>
          ) : (
            <div className={md.desktopSettingsDataGrid}>
              <div className={s.settingsDataCard}>
                <strong>Импорт клиентов</strong>
                <p className={s.docSectionHint}>
                  Загрузка CSV с колонками full_name, email, phone и др.
                </p>
                <Button type="button" onClick={() => setImportOpen(true)}>
                  Импорт CSV
                </Button>
              </div>
              <div className={s.settingsDataCard}>
                <strong>Экспорт клиентов</strong>
                <p className={s.docSectionHint}>
                  Скачать реестр для редактирования и повторного импорта
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={async () => {
                    try {
                      const res = await clinicAPI.exportPatientsCsv();
                      const url = URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "patients.csv";
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success("CSV скачан");
                    } catch (e) {
                      toast.error(e.response?.data?.error || "Ошибка");
                    }
                  }}
                >
                  Скачать CSV
                </Button>
              </div>
              <Link to="/manage/app/clients" className={s.settingsDataCard}>
                <strong>Реестр клиентов</strong>
                <span className={s.docSectionHint}>Поиск, дубликаты, карточки</span>
              </Link>
              <a
                href={CRM_USER_GUIDE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={s.settingsDataCard}
              >
                <strong>{CRM_USER_GUIDE_LABEL}</strong>
                <span className={s.docSectionHint}>{CRM_USER_GUIDE_HINT}</span>
              </a>
            </div>
          )}
        </div>
      )}
    </>
  );

  if (tabFromUrl === "registration") {
    return <Navigate to="/manage/app/clients/registration" replace />;
  }

  if (loading) {
    const skeleton = <SkeletonCard />;
    return isApp ? (
      <HubScreen title="Настройки">
        {skeleton}
      </HubScreen>
    ) : (
      <div className={md.desktopSettingsPage}>{skeleton}</div>
    );
  }

  if (isApp) {
    const roleLabel =
      normalizeRole(role) === "owner"
        ? "Владелец"
        : normalizeRole(role) === "admin"
          ? "Администратор"
          : "Сотрудник";

    return (
      <HubScreen title="Настройки">
        {hasAdminAccess(role) || owner ? (
          <button
            type="button"
            className={aa.moreProfile}
            onClick={() => navigate("/manage/app/profile")}
          >
            <Avatar
              name={profile?.full_name || "?"}
              src={mediaUrl(profile?.photo_url)}
              size="md"
              className={aa.moreAvatarImg}
            />
            <div className={aa.moreProfileInfo}>
              <p className={aa.moreProfileName}>{profile?.full_name || "—"}</p>
              <p className={aa.moreProfileRole}>
                {roleLabel} · Профиль
              </p>
            </div>
            <span className={aa.moreProfileChevron} aria-hidden />
          </button>
        ) : null}

        <div className={aa.settingsHubClubRow}>
          {form.logo_url ? (
            <img src={form.logo_url} alt="" className={aa.settingsHubClubLogo} />
          ) : (
            <div className={aa.settingsHubClubLogoPh}>
              {(form.name || "К").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className={aa.settingsHubClubMeta}>
            <strong>{form.name || "Клуб"}</strong>
            <span>
              {form.subdomain ? `${form.subdomain} · ` : ""}
              {STATUS_LABELS[form.status] || form.status || "—"}
            </span>
          </div>
        </div>

        <HubSegment tabs={visibleTabs} active={tab} onChange={setTabAndUrl} />
        {tabPanels}

        <AdminPatientImport
          open={importOpen}
          onClose={() => setImportOpen(false)}
          appMode={isApp}
        />
      </HubScreen>
    );
  }

  return (
    <div className={md.desktopSettingsPage}>
      <div className={md.desktopSettingsPanel}>
        <header className={md.desktopSettingsHead}>
          <div className={md.desktopSettingsClub}>
            {form.logo_url ? (
              <img src={form.logo_url} alt="" className={md.desktopSettingsClubLogo} />
            ) : (
              <div className={md.desktopSettingsClubLogoPh}>
                {(form.name || "К").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className={md.desktopSettingsClubText}>
              <p className={md.desktopSettingsClubName}>{form.name || "Клуб"}</p>
              <div className={md.desktopSettingsClubMetaRow}>
                {form.subdomain ? (
                  <span className={md.desktopSettingsChip}>{form.subdomain}</span>
                ) : null}
                <span
                  className={[
                    md.desktopSettingsStatus,
                    form.status === "active"
                      ? md.desktopSettingsStatusActive
                      : form.status === "suspended"
                        ? md.desktopSettingsStatusSuspended
                        : md.desktopSettingsStatusTrial,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {STATUS_LABELS[form.status] || form.status || "—"}
                </span>
                {form.phone ? (
                  <span className={md.desktopSettingsMetaMuted}>{form.phone}</span>
                ) : null}
              </div>
            </div>
          </div>
          <div className={md.desktopSettingsHeadActions}>
            <Button onClick={save} disabled={saving}>
              {saving ? "Сохранение…" : "Сохранить"}
            </Button>
          </div>
        </header>

        <div className={md.desktopSettingsTabs}>
          <Tabs items={visibleTabs} active={tab} onChange={setTabAndUrl} />
        </div>

        <div className={md.desktopSettingsBody}>{tabPanels}</div>
      </div>

      <AdminPatientImport open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}
